import { I18n } from '@lingui/core';
import {
  AttendeeFragment,
  AttendeeSessionType,
  PublicEWebinarFragment,
  ReactionForAttendeeFragment,
  RegistrantFragment,
  SessionType,
  ThemeSettingsFragment,
} from '../GraphQL/schema/types';
import config from '../config';
import {
  getReadableColorOnWhite,
  readableColorOnBackground,
} from '../shared/frontend/utils/tinyColor';
import { RegistrantApiResponse } from '../widget/src/interface';
import { packData } from './encrypt';
import { getSessionTimeString } from './getSessionTimeString';

const packThankyouUrl = async (url: string): Promise<string> => {
  const unpackedUrl = new URL(url);
  const params: Record<string, string> = {};
  unpackedUrl.searchParams.forEach((value, key) => {
    params[key] = value;
  });
  const packedUrl = new URL(unpackedUrl.origin + unpackedUrl.pathname);
  packedUrl.searchParams.set('data', await packData(params, config.PACK_ATTENDEE_DATA_KEY));
  return packedUrl.href;
};

export const getRedirectUrl = async (
  attendee: AttendeeFragment | RegistrantApiResponse['attendee'],
  ewebinar: Pick<
    PublicEWebinarFragment,
    'scheduleSettings' | 'thankyouPageSettings' | 'viewerRoomSettings'
  > & {
    themeSettings: Pick<ThemeSettingsFragment, 'primaryColor' | 'borderRadius' | 'actionColor'>;
  },
  localI18n?: I18n
): Promise<string> => {
  const { EDate } = await import('../shared/frontend/utils/EDate');

  // DD: I've seen redirectionUrl be the empty string ''
  let urlRedirect = ewebinar.thankyouPageSettings?.redirectionUrl || attendee.urls.thankyou;
  const isOnDemand = Boolean(attendee.isOnDemand);
  const shouldPackAttendeeData =
    !ewebinar.thankyouPageSettings?.redirectionUrl ||
    ewebinar.thankyouPageSettings?.packAttendeeData;

  const joinLink =
    attendee.sessionType === SessionType.Replay ? attendee.urls.replay : attendee.urls.join;

  try {
    const redirectUrls = new URL(urlRedirect);
    const zuluFormat = "yyyy-MM-dd'T'HH:mm:ss'Z'";
    const time =
      attendee.sessionType === SessionType.Replay
        ? 'replay'
        : new EDate(attendee.startTime!).format(zuluFormat);
    const [sessionText] = await getSessionTimeString(
      attendee.startTime ?? undefined,
      ewebinar.viewerRoomSettings.language,
      isOnDemand,
      undefined,
      localI18n
    );
    redirectUrls.searchParams.append('sessionText', sessionText);
    redirectUrls.searchParams.append('primaryColor', ewebinar.themeSettings.primaryColor ?? '');
    redirectUrls.searchParams.append('language', ewebinar.viewerRoomSettings.language || 'en');
    redirectUrls.searchParams.append(
      'readableColor',
      readableColorOnBackground(ewebinar.themeSettings.primaryColor ?? '')
    );
    redirectUrls.searchParams.append(
      'readableOnWhiteColor',
      getReadableColorOnWhite(ewebinar.themeSettings.primaryColor ?? '')
    );
    redirectUrls.searchParams.append('borderRadius', `${ewebinar.themeSettings.borderRadius}`);
    redirectUrls.searchParams.append('firstName', attendee.firstName ?? '');
    redirectUrls.searchParams.append('lastName', attendee.lastName ?? '');
    redirectUrls.searchParams.append('email', attendee.email ?? '');
    redirectUrls.searchParams.append('joinLink', joinLink);
    redirectUrls.searchParams.append('session', time);
    redirectUrls.searchParams.append('attendeeId', attendee.id);
    redirectUrls.searchParams.append(
      'isReplay',
      attendee.sessionType === SessionType.Replay ? '1' : '0'
    );
    if (attendee.utmParams) {
      for (const [key, value] of Object.entries(attendee.utmParams)) {
        if (value && key !== '__typename') {
          redirectUrls.searchParams.append(key, value);
        }
      }
    }
    urlRedirect = redirectUrls.href;
  } catch {
    console.error('Invalid redirectionUrl');
  }

  if (shouldPackAttendeeData) {
    urlRedirect = await packThankyouUrl(urlRedirect);
  }

  if (
    ewebinar.thankyouPageSettings.skipForReplayAndJIT &&
    attendee.sessionType !== SessionType.Scheduled
  ) {
    urlRedirect = joinLink;
  }

  return urlRedirect;
};

export function getSessionTypeString(
  registrant: RegistrantFragment | ReactionForAttendeeFragment | AttendeeFragment
) {
  if (registrant?.isOnDemand) return 'On-demand';

  switch (registrant.sessionType as unknown as AttendeeSessionType) {
    case AttendeeSessionType.Scheduled:
      return 'Scheduled';
    case AttendeeSessionType.JustInTime:
      return 'Just-in-time';
    case AttendeeSessionType.Replay:
      return 'Replay';
  }

  return 'Just-in-time';
}
